import React, { useEffect, useRef, useState } from 'react';
import { Search, Trash2, ImagePlus } from 'lucide-react';
import {
  LUCIDE_ICONS,
  SIMPLE_ICONS,
  getLucideComponent,
  getSimpleIconComponent,
  getSimpleIconsForSearch,
} from '../../lib/image-editor/iconRegistry';
import type {
  UseImageEditorResult,
  IconBlock,
} from '../../hooks/useImageEditor';
import type { IconShape } from '../../lib/image-editor/imagePipeline';
import { ICON_QR_SIZE_MIN } from '../../lib/image-editor/overlayLimits';

const ICON_SHAPE_OPTIONS: { value: IconShape; label: string }[] = [
  { value: 'none', label: '無' },
  { value: 'circle', label: '圓形' },
  { value: 'square', label: '方形' },
  { value: 'rounded', label: '圓角' },
];

interface IconPanelProps {
  editor: UseImageEditorResult;
  /** 圖示大小滑桿下限（依畫布動態傳入，預設 16） */
  sizeMin?: number;
  /** 圖示大小滑桿上限（依畫布動態傳入，預設 120） */
  sizeMax?: number;
}

function matchIconSearch(keyword: string, ...strings: string[]): boolean {
  const k = keyword.trim().toLowerCase();
  if (!k) return true;
  return strings.some(s => (s ?? '').toLowerCase().includes(k));
}

/**
 * 圖示設定：Lucide 圖示與 Simple Icons 品牌 Logo，可多個、可拖曳定位於預覽畫布。
 * 搜尋時從「完整圖示庫」過濾（介面未列出的全量），非僅過濾畫面上的常用圖示。
 */
export function IconPanel({
  editor,
  sizeMin = ICON_QR_SIZE_MIN,
  sizeMax = 400,
}: IconPanelProps) {
  const { iconItems, addIconItem, removeIconItem, updateIconItem } = editor;
  const minSize = Math.max(ICON_QR_SIZE_MIN, sizeMin);
  const maxSize = Math.max(minSize, sizeMax);

  const [iconSearch, setIconSearch] = useState('');
  const [fullLucideNames, setFullLucideNames] = useState<string[] | null>(null);
  const [fullSimpleList, setFullSimpleList] = useState<
    { slug: string; title: string; siKey: string }[] | null
  >(null);
  const [lucideModule, setLucideModule] = useState<Record<
    string,
    React.ComponentType<unknown>
  > | null>(null);
  const [simpleIconsModule, setSimpleIconsModule] = useState<Record<
    string,
    React.ComponentType<unknown>
  > | null>(null);
  const [loadingFullLists, setLoadingFullLists] = useState(false);

  useEffect(() => {
    if (!iconSearch.trim()) return;
    setLoadingFullLists(true);
    let cancelled = false;
    (async () => {
      const [lucideMod, simpleList, packMod] = await Promise.all([
        import('lucide-react'),
        getSimpleIconsForSearch(),
        import('@icons-pack/react-simple-icons'),
      ]);
      if (cancelled) return;
      const lucide = lucideMod as unknown as {
        icons?: Record<string, React.ComponentType<unknown>>;
      } & Record<string, React.ComponentType<unknown>>;
      // 使用官方 icons 物件取得完整圖示名稱，避免 Object.keys(lucide) 在部分打包環境只含部分匯出
      const names = lucide.icons
        ? Object.keys(lucide.icons)
        : Object.keys(lucide).filter(
            k =>
              k !== 'default' &&
              k !== 'icons' &&
              k[0] === k[0].toUpperCase() &&
              typeof lucide[k] === 'function'
          );
      if (cancelled) return;
      setFullLucideNames(names);
      setFullSimpleList(simpleList);
      setLucideModule(lucide);
      setSimpleIconsModule(
        packMod as unknown as Record<string, React.ComponentType<unknown>>
      );
      setLoadingFullLists(false);
    })();
    return () => {
      cancelled = true;
      setLoadingFullLists(false);
    };
  }, [iconSearch.trim()]);

  const hasSearch = iconSearch.trim().length > 0;
  const filteredLucideFromFull =
    hasSearch && fullLucideNames
      ? fullLucideNames.filter(name => matchIconSearch(iconSearch, name))
      : [];
  const filteredSimpleFromFull =
    hasSearch && fullSimpleList
      ? fullSimpleList.filter(({ slug, title }) =>
          matchIconSearch(iconSearch, slug, title)
        )
      : [];

  const showCommon = !hasSearch;
  const showSearchResults = hasSearch;

  const handleAddLucide = (id: string) => {
    addIconItem({
      source: 'lucide',
      iconId: id,
      size: 48,
      color: '#000000',
      opacity: 0.9,
      anchorX: 0.5,
      anchorY: 0.5,
    });
  };

  const handleAddSimple = (slug: string) => {
    addIconItem({
      source: 'simple',
      iconId: slug,
      size: 48,
      color: '#000000',
      opacity: 0.9,
      anchorX: 0.5,
      anchorY: 0.5,
    });
  };

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const handleUploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      addIconItem({
        source: 'custom',
        imageDataUrl: dataUrl,
        size: 48,
        color: '#000000',
        opacity: 0.9,
        anchorX: 0.5,
        anchorY: 0.5,
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">
          點擊圖示加入畫布，於預覽區拖曳可調整位置
        </p>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUploadImage}
            aria-label="上傳圖片作為圖示"
          />
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:border-primary dark:hover:bg-primary/10"
          >
            <ImagePlus className="h-4 w-4 shrink-0" aria-hidden />
            上傳圖片
          </button>
          <label className="flex flex-1 min-w-0 items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400">
            <Search className="h-4 w-4 shrink-0" aria-hidden />
            <input
              type="search"
              value={iconSearch}
              onChange={e => setIconSearch(e.target.value)}
              placeholder="搜尋圖示（輸入關鍵字從完整圖示庫搜尋）"
              className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
              aria-label="搜尋圖示"
            />
          </label>
        </div>

        {showCommon && (
          <>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              常用圖示 (Lucide)
            </p>
            <div className="flex flex-wrap gap-2">
              {LUCIDE_ICONS.map(({ id, label }) => {
                const Icon = getLucideComponent(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleAddLucide(id)}
                    className="flex h-10 w-10 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-primary dark:hover:bg-primary/10"
                    title={label}
                    aria-label={`加入 ${label}`}
                  >
                    {Icon ? <Icon size={20} /> : null}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              常用品牌 Logo (Simple Icons)
            </p>
            <div className="flex flex-wrap gap-2">
              {SIMPLE_ICONS.map(({ slug, label }) => {
                const Icon = getSimpleIconComponent(slug);
                return (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => handleAddSimple(slug)}
                    className="flex h-10 w-10 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-primary dark:hover:bg-primary/10"
                    title={label}
                    aria-label={`加入 ${label}`}
                  >
                    {Icon ? <Icon size={20} /> : null}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {showSearchResults && (
          <>
            {loadingFullLists ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                載入圖示庫中…
              </p>
            ) : (
              <>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  搜尋結果（Lucide，共 {filteredLucideFromFull.length} 個）
                </p>
                <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
                  {filteredLucideFromFull.length > 0 ? (
                    filteredLucideFromFull.map(name => {
                      const Icon =
                        (
                          lucideModule as {
                            icons?: Record<
                              string,
                              React.ComponentType<unknown>
                            >;
                          }
                        )?.icons?.[name] ??
                        (
                          lucideModule as Record<
                            string,
                            React.ComponentType<unknown>
                          >
                        )?.[name];
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => handleAddLucide(name)}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-primary dark:hover:bg-primary/10"
                          title={name}
                          aria-label={`加入 ${name}`}
                        >
                          {Icon ? (
                            React.createElement(
                              Icon as React.ComponentType<{ size?: number }>,
                              { size: 20 }
                            )
                          ) : (
                            <span className="text-[10px]">
                              {name.slice(0, 2)}
                            </span>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      沒有符合的 Lucide 圖示
                    </p>
                  )}
                </div>
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  搜尋結果（品牌 Logo，共 {filteredSimpleFromFull.length} 個）
                </p>
                <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
                  {filteredSimpleFromFull.length > 0 ? (
                    filteredSimpleFromFull.map(({ slug, title, siKey }) => {
                      const reactKey = 'S' + siKey.slice(1);
                      const Icon = simpleIconsModule?.[reactKey];
                      return (
                        <button
                          key={slug}
                          type="button"
                          onClick={() => handleAddSimple(slug)}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-primary dark:hover:bg-primary/10"
                          title={title}
                          aria-label={`加入 ${title}`}
                        >
                          {Icon ? (
                            React.createElement(
                              Icon as React.ComponentType<{ size?: number }>,
                              { size: 20 }
                            )
                          ) : (
                            <span className="text-[10px]">
                              {slug.slice(0, 2)}
                            </span>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      沒有符合的品牌 Logo
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {iconItems.filter((b: IconBlock) => b.source !== 'doodle').length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            已加入的圖示 (
            {iconItems.filter((b: IconBlock) => b.source !== 'doodle').length})
          </p>
          {iconItems
            .filter((block: IconBlock) => block.source !== 'doodle')
            .map((block: IconBlock) => (
              <div
                key={block.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-800/50"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 dark:border-gray-600">
                  {block.source === 'custom' && block.imageDataUrl ? (
                    <img
                      src={block.imageDataUrl}
                      alt=""
                      className="h-full w-full object-contain"
                    />
                  ) : block.source === 'doodle' && block.iconId ? (
                    <img
                      src={block.iconId}
                      alt=""
                      className="h-full w-full object-contain"
                      crossOrigin="anonymous"
                    />
                  ) : block.source === 'lucide' ? (
                    (() => {
                      const Icon = getLucideComponent(block.iconId ?? '');
                      return Icon ? (
                        <Icon size={20} color={block.color} />
                      ) : null;
                    })()
                  ) : (
                    (() => {
                      const Icon = getSimpleIconComponent(block.iconId ?? '');
                      return Icon ? (
                        <Icon size={20} color={block.color} />
                      ) : null;
                    })()
                  )}
                </span>
                <div className="min-w-0 flex-1 space-y-3">
                  {block.source !== 'custom' && block.source !== 'doodle' && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        顏色
                      </span>
                      <input
                        type="color"
                        value={block.color}
                        onChange={e =>
                          updateIconItem(block.id, { color: e.target.value })
                        }
                        className="h-7 w-9 cursor-pointer rounded border border-gray-300 bg-transparent p-0"
                        aria-label="顏色"
                      />
                    </div>
                  )}
                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                      <span>大小 (px)</span>
                      <span className="tabular-nums">
                        {Math.max(minSize, Math.min(maxSize, block.size))} px
                      </span>
                    </div>
                    <input
                      type="range"
                      min={minSize}
                      max={maxSize}
                      step={Math.max(1, Math.round((maxSize - minSize) / 50))}
                      value={Math.max(minSize, Math.min(maxSize, block.size))}
                      onChange={e =>
                        updateIconItem(block.id, {
                          size: Number(e.target.value),
                        })
                      }
                      className="w-full accent-primary"
                      aria-label="圖示大小"
                    />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                      <span>不透明度</span>
                      <span className="tabular-nums">
                        {Math.round(block.opacity * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round(block.opacity * 100)}
                      onChange={e =>
                        updateIconItem(block.id, {
                          opacity: Number(e.target.value) / 100,
                        })
                      }
                      className="w-full accent-primary"
                      aria-label="不透明度"
                    />
                  </div>
                  <div>
                    <span className="mb-1 block text-xs text-gray-600 dark:text-gray-400">
                      形狀
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {ICON_SHAPE_OPTIONS.map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() =>
                            updateIconItem(block.id, { shape: value })
                          }
                          className={`rounded px-2.5 py-1 text-xs ${
                            (block.shape ?? 'none') === value
                              ? 'bg-primary text-white'
                              : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                          }`}
                          aria-label={`形狀：${label}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeIconItem(block.id)}
                  className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700 dark:hover:text-red-400"
                  aria-label="移除此圖示"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        圖示會繪製於畫布上，順序在邊線與畫布尺寸之後、文字浮水印之前。
      </p>
    </div>
  );
}
